// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./DetailLibrary.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { deleteAPICall, getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";
import { UserPreferencesContext } from "../../context/UserPreferencesContext";

// MUI COMPONENTS
import {
	Button,
	ButtonGroup,
	CircularProgress,
	IconButton,
} from "@mui/material";

// MUI ICONS
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

// CUSTOM COMPONENTS
import NewDetailDialog from "./NewDetailDialog/NewDetailDialog";
import ThumbnailBlowupDialog from "./ThumbnailBlowupDialog/ThumbnailBlowupDialog";
import TanStackTable from "../Misc/Table/TanStackTable";
import J2NoResultsFound from "../Misc/J2NoResultsFound/J2NoResultsFound";
import AssembliesGroupingsSearchBar from "./AssembliesGroupingsSearchBar/AssembliesGroupingsSearchBar";
import DetailLibrarySearchBar from "./DetailLibrarySearchBar/DetailLibrarySearchBar";

// CONSTANTS
import {
	checkboxSort,
	splitSort,
	strictSort,
	notApplicable,
	defaultSort,
} from "../Misc/Table/TanStackTableConstants";

// FUNCTIONAL COMPONENT
const DetailLibrary = () => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);
	const {
		prefs,
		setPref,
		loading: prefsLoading,
	} = useContext(UserPreferencesContext);

	// STATES
	// API data
	const [detailLibraryRecords, setDetailLibraryRecords] = useState([]);

	const [detailLibraryRecordsRows, setDetailLibraryRecordsRows] = useState(
		[]
	);
	const [detailLibraryRecordsColumns, setDetailLibraryRecordsColumns] =
		useState([]);

	const [favorites, setFavorites] = useState([]);
	const [assemblies, setAssemblies] = useState([]);
	const [groupings, setGroupings] = useState([]);
	const [assemblyGroupings, setAssemblyGroupings] = useState([]); // Junction table
	const [statuses, setStatuses] = useState([]);

	// Table Version and filters
	const [searchVersion, setSearchVersion] = useState(null); // set after prefs load
	const [resetAdvancedFilters, setResetAdvancedFilters] = useState(
		() => () => {}
	);

	// Search bar
	const [showAssemblyGroupingSearchBar, setShowAssemblyGroupingSearchBar] =
		useState(false);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// Dialogs
	const [newRecordOpen, setNewRecordOpen] = useState(false);

	const [thumbnailBlowupDialogOpen, setThumbnailBlowupDialogOpen] =
		useState(false);
	const [thumbnailURL, setThumbnailURL] = useState(""); // [TODO] - Set this to the URL of the thumbnail that was clicked on

	// USE EFFECT
	useEffect(() => {
		fetchDetailLibraryRecords();
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Detail Library",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the Detail Library page",
		});
	}, []);

	useEffect(() => {
		if (
			detailLibraryRecords.length > 0 &&
			statuses.length > 0 &&
			assemblies.length > 0
		) {
			createRowData(detailLibraryRecords);
			createColumnData();
		}
	}, [detailLibraryRecords, assemblies, groupings, statuses, favorites]);

	useEffect(() => {
		// reset filtered records when search version changes
		if (searchVersion === 1) {
			// reset to legacy search bar
			resetAdvancedFilters(); // this should call setColumnFilters([]) and close popovers
		} else if (searchVersion === 2) {
			// reset to advanced search bar
			setShowAssemblyGroupingSearchBar(false);
			createRowData(detailLibraryRecords);
		}
	}, [searchVersion]);

	// when prefs load/change, sync initial view
	useEffect(() => {
		if (
			prefs &&
			typeof prefs.legacy_detail_library_search !== "undefined"
		) {
			setSearchVersion(prefs.legacy_detail_library_search ? 1 : 2);
		}
	}, [prefs]);

	// INPUT HANDLERS
	const newRecordClicked = () => {
		setNewRecordOpen(!newRecordOpen);
	};

	const newDetailDialogClosed = () => {
		setNewRecordOpen(false);
	};

	// Thumnail blowup
	const openThumbnailBlowupDialog = (url) => {
		setThumbnailBlowupDialogOpen(true);
		setThumbnailURL(url);
	};

	const closeThumbnailBlowupDialog = () => {
		setThumbnailBlowupDialogOpen(false);
	};

	// API FUNCTIONS
	const fetchDetailLibraryRecords = async () => {
		setIsLoading(true);

		await getDetails();
		await getAssemblies();
		await getGroupings();
		await getAssembliesGroupings();
		await getStatuses();
		await getFavorites();

		setIsLoading(false);
	};

	const getAssembliesGroupings = async () => {
		const assembliesGroupingsRes = await getAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/groupingJunction"
		);
		setAssemblyGroupings(assembliesGroupingsRes.data);
	};

	const getGroupings = async () => {
		const groupingsRes = await getAPICall(
			instance,
			accounts[0],
			"/api/encompass/groupings/all"
		);
		setGroupings(groupingsRes.data);
	};

	const getDetails = async (sortBy, sortDirection) => {
		let queryParams;
		if (sortBy && sortDirection) {
			queryParams = `sortBy=${sortBy}&sortDirection=${sortDirection}`;
		}

		const detailRes = await getAPICall(
			instance,
			accounts[0],
			`/api/encompass/details/all?${queryParams}`
		);
		setDetailLibraryRecords(detailRes.data);
	};

	const getAssemblies = async () => {
		const assembliesRes = await getAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/all"
		);
		setAssemblies(assembliesRes.data);
	};

	const getStatuses = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/specifications/statuses/all"
			);
			setStatuses(res.data);
		} catch (error) {
			console.log("Error in fetchStatuses: ", error);
		}
	};

	const getFavorites = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/encompass/details/favorites/byEmployeeID",
				{
					employeeID: employeeID,
				}
			);
			setFavorites(res.data);
		} catch (error) {
			console.log("Error in getFavorites: ", error);
		}
	};

	const addFavorite = async (detailID) => {
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/encompass/details/favorites/add",
				{
					employeeID: employeeID,
					detailID: detailID,
					modifiedBy: employeeID,
				}
			);
		} catch (error) {
			console.log("Error in addFavorite: ", error);
		}
	};

	const removeFavorite = async (detailID) => {
		try {
			const res = await deleteAPICall(
				instance,
				accounts[0],
				"/api/encompass/details/favorites/delete",
				{
					employeeID: employeeID,
					detailID: detailID,
				}
			);
		} catch (error) {
			console.log("Error in removeFavorite: ", error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		const returnPDFThumbnail = (record) => {
			return (
				<div className="pdfThumbnail">
					<img
						src={record.thumbnail_link}
						alt="PDF Thumbnail"
						onMouseEnter={() =>
							openThumbnailBlowupDialog(record.thumbnail_link)
						}
					/>
				</div>
			);
		};

		const toggleFavorite = async (detailID) => {
			const isFavorite = favorites.some(
				(fav) => fav.detail_id === detailID
			);
			if (isFavorite) {
				await removeFavorite(detailID);
				const updatedFavorites = favorites.filter(
					(fav) => fav.detail_id !== detailID
				);
				setFavorites(updatedFavorites);
			} else {
				await addFavorite(detailID);
				const newFavorite = {
					detail_id: detailID,
					employee_id: employeeID,
				};
				setFavorites((favorites) => [...favorites, newFavorite]);
			}
		};

		setDetailLibraryRecordsColumns([
			{
				accessorKey: "id",
				header: () => <span>ID</span>,
				label: "ID",
				cell: (props) => <p>{props.getValue() ?? notApplicable}</p>,
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "title",
				header: () => <span>Title</span>,
				label: "Title",
				cell: ({ row }) =>
					row.original.title && row.original.id ? (
						<a
							href={`/design/detail_library/${row.original.id}`}
						>
							{row.original.title}
						</a>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "assemblies",
				header: () => <span>Assemblies</span>,
				label: "Assemblies",
				cell: ({ row }) => (
					<div>
						{row.original.assemblies
							? row.original.assemblies
									.split("_SPLIT_")
									.map((assembly, idx) => (
										<div key={idx}>{assembly}</div>
									))
							: notApplicable}
					</div>
				),
				filterType: "checkbox",
				filterOptions: assemblies.map((assembly) => ({
					id: assembly.id,
					label: assembly.name,
				})),
				filterFn: splitSort,
			},
			{
				accessorKey: "groupings",
				header: () => <span>Groupings</span>,
				label: "Groupings",
				cell: ({ row }) => (
					<div>
						{row.original.groupings
							? row.original.groupings
									.split("_SPLIT_")
									.map((grouping, idx) => (
										<div key={idx}>{grouping}</div>
									))
							: notApplicable}
					</div>
				),
				filterType: "checkbox",
				filterOptions: groupings.map((grouping) => ({
					id: grouping.id,
					label: grouping.name,
				})),
				filterFn: splitSort,
			},
			{
				accessorKey: "download_count",
				header: () => <span>Download Count</span>,
				label: "Download Count",
				cell: (props) => <p>{props.getValue() ?? notApplicable}</p>,
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "detail_status_name",
				header: () => <span>Status</span>,
				label: "Status",
				cell: (props) =>
					props.getValue() ? (
						<p
							style={{
								color: props.row.original.detail_status_color,
							}}
						>
							{props.getValue()}
						</p>
					) : (
						notApplicable
					),
				filterType: "checkbox",
				filterOptions: statuses.map((status) => ({
					id: status.id,
					label: status.name,
				})),
				filterFn: checkboxSort,
			},
			{
				accessorKey: "thumbnail_link",
				header: () => <span>PDF</span>,
				label: "PDF",
				cell: (props) => (
					<div className="pdfThumbnail">
						{props.getValue()
							? returnPDFThumbnail(props.row.original)
							: notApplicable}
					</div>
				),
				enableSorting: false,
			},
			{
				accessorKey: "is_favorite",
				header: () => <span>Favorite</span>,
				label: "Favorite",
				cell: ({ row }) => {
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "flex-start",
							}}
						>
							<IconButton
								onClick={() => toggleFavorite(row.original.id)}
								sx={{ padding: 0, margin: 0 }}
								color="primary"
							>
								{row.original.is_favorite ? (
									<StarIcon />
								) : (
									<StarBorderIcon />
								)}
							</IconButton>
						</div>
					);
				},
				filterType: "checkbox",
				filterOptions: [
					{ label: "Favorite", value: true },
					{ label: "Not Favorite", value: false },
				],
				filterFn: checkboxSort,
			},
		]);
	};

	const createRowData = (records) => {
		setDetailLibraryRecordsRows(
			records.map((record) => ({
				id: record.id,
				title: record.title,
				assemblies: record.split_assemblies,
				groupings: record.split_groupings,
				download_count: record.download_count,
				detail_status_name: record.detail_status_name,
				detail_status_color: record.detail_status_color,
				thumbnail_link: record.thumbnail_link,
				is_favorite: favorites.some(
					(fav) => fav.detail_id === record.id
				),
			}))
		);
	};

	let isSubset = (subsetArray, parentArray) => {
		if (subsetArray.length === 0) {
			return true;
		}

		return subsetArray.every((el) => {
			return parentArray.includes(el);
		});
	};

	// Search bar
	const assembliesGroupingsSelected = () => {
		setShowAssemblyGroupingSearchBar(true);
	};

	const assembliesGroupingsUnselected = () => {
		setShowAssemblyGroupingSearchBar(false);
	};

	// filtering
	const filterDetailLibraryRecords = (searchValue, searchByParameter) => {
		setIsLoading(true);

		if (!searchValue || searchValue.trim() === "") {
			// Show all records if input is empty
			createRowData(detailLibraryRecords);
			setIsLoading(false);
			return;
		}

		let filteredRecords = detailLibraryRecords.filter((record) => {
			if (searchByParameter === "title") {
				return (record.title ?? "")
					.toLowerCase()
					.includes(searchValue.toLowerCase());
			} else if (searchByParameter === "id") {
				return record.id == searchValue;
			}
			return false;
		});

		createRowData(filteredRecords);
		setIsLoading(false);
	};

	const filterAssemblyGroupingsSearchBar = async (
		selectedAssemblies,
		selectedGroupings
	) => {
		setIsLoading(true);

		let filteredRecords = [];

		let selectedAssemblyIDs = selectedAssemblies.map(
			(assembly) => assembly.id
		);
		let selectedGroupingIDs = selectedGroupings.map(
			(grouping) => grouping.id
		);

		for (let record of detailLibraryRecords) {
			var assemblyIDs = (record.assembly_ids ?? "").split(",");
			var groupingIDs = (record.grouping_ids ?? "").split(",");

			assemblyIDs =
				assemblyIDs.length === 1 && assemblyIDs[0] === ""
					? []
					: assemblyIDs;
			groupingIDs =
				groupingIDs.length === 1 && groupingIDs[0] === ""
					? []
					: groupingIDs;

			assemblyIDs = assemblyIDs.map((id) => parseInt(id));
			groupingIDs = groupingIDs.map((id) => parseInt(id));

			if (
				isSubset(selectedAssemblyIDs, assemblyIDs) &&
				isSubset(selectedGroupingIDs, groupingIDs)
			) {
				filteredRecords.push(record);
			}
		}

		createRowData(filteredRecords);

		setIsLoading(false);
	};

	// RENDER
	return (
		<div className="DetailLibrary">
			<div className="detailLibraryHeader">
				<div className="detailLibraryHeaderTitle">
					Detail Library
					<div className="detailLibraryHeaderButtons">
						{!prefsLoading && searchVersion !== null && (
							<ButtonGroup>
								<Button
									onClick={() => {
										setSearchVersion(1);
										// persist preference: true = legacy
										setPref(
											"legacy_detail_library_search",
											true
										);
									}}
									variant={
										searchVersion === 1
											? "contained"
											: "outlined"
									}
								>
									Legacy
								</Button>
								<Button
									onClick={() => {
										setSearchVersion(2);
										// persist preference: false = advanced
										setPref(
											"legacy_detail_library_search",
											false
										);
									}}
									variant={
										searchVersion === 2
											? "contained"
											: "outlined"
									}
								>
									Advanced
								</Button>
							</ButtonGroup>
						)}
						<Button
							variant="contained"
							color="primary"
							onClick={newRecordClicked}
							disabled={isLoading}
						>
							New Record
						</Button>
					</div>
				</div>
			</div>
			{/* LEGACY TABLE SEARCH BAR (advanced search comes with tanstack table) */}
			{searchVersion === 1 && (
				<div className="legacyTableContainer">
					{showAssemblyGroupingSearchBar && (
						<AssembliesGroupingsSearchBar
							filterAssemblyGroupingsSearchBar={(
								selectedAssemblies,
								selectedGroupings
							) =>
								filterAssemblyGroupingsSearchBar(
									selectedAssemblies,
									selectedGroupings
								)
							}
							assembliesGroupingsUnselected={
								assembliesGroupingsUnselected
							}
							assemblies={assemblies}
							groupings={groupings}
						/>
					)}
					{!showAssemblyGroupingSearchBar && (
						<DetailLibrarySearchBar
							filterDetailLibraryRecords={(
								searchValue,
								searchByParameter
							) =>
								filterDetailLibraryRecords(
									searchValue,
									searchByParameter
								)
							}
							assembliesGroupingsSelected={
								assembliesGroupingsSelected
							}
						/>
					)}
				</div>
			)}
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{detailLibraryRecords.length === 0 ? (
						<J2NoResultsFound />
					) : (
						<TanStackTable
							columnData={detailLibraryRecordsColumns}
							rowData={detailLibraryRecordsRows}
							defaultSort={[
								{
									id: "is_favorite",
									desc: true,
								},
							]}
							showSearch={searchVersion == 1 ? false : true} // Show search only for advanced table
							setFilterResetter={setResetAdvancedFilters}
						/>
					)}
				</div>
			)}
			<NewDetailDialog
				open={newRecordOpen}
				newDetailDialogClosed={newDetailDialogClosed}
				assemblies={assemblies}
				groupings={groupings}
				assemblyGroupings={assemblyGroupings}
				fetchDetailLibraryRecords={fetchDetailLibraryRecords}
			/>
			<ThumbnailBlowupDialog
				open={thumbnailBlowupDialogOpen}
				closeThumbnailBlowupDialog={closeThumbnailBlowupDialog}
				thumbnailURL={thumbnailURL}
			/>
		</div>
	);
};

export default DetailLibrary;
